import { useMemo, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getUserRole,
  formatAmountWithTwoDecimalPlaces,
} from '../../../shared/utils/function';
import { DEFAULT_USER_AVATAR, ROLE } from '../../../shared/constants/common';
import {
  getActivityIcon,
  getActivityBadge,
  parseDaysLeft,
  renderActivityMessage,
  renderTitleWithMentions,
  resolveActivitySubscriberUserId,
  shouldShowActivityDescription,
} from '../../../shared/utils/activityUtil';
import { isMongoObjectIdString } from '../../../shared/utils/chatUtil';
import { useNavigation } from '../../../shared/utils/useNavigation';
import {
  ACTIVITY_PAGE_LIMIT,
  ACTIVITY_TITLE_MAP,
  TITLES_WITH_INLINE_MENTIONS,
  ACTIVITY_ARCHIVED_SLICE_KEY,
} from '../../../shared/constants/activity';
import {
  fetchCreatorActivityTab,
  markActivityNotificationViewed,
} from '../../../shared/actions/activityAction';
import { extendTipDeadline } from '../../../shared/actions/tipAction';
import { reportError } from '../../../shared/sentry';
import { navigateToPushLink } from '../../../shared/pusNotification/fcm/pushNavigation';

/**
 * Subscription (Subscribers tab) and PPV (Followers tab): when the API omits a photo or
 * sends the server default path, show the app default user art instead of a generic icon.
 */
const ACTIVITY_TYPES_DEFAULT_AVATAR = new Set([
  'new_follower',
  'new_subscribers',
  'subscription',
]);

function getActivityCardNavigationMeta(item, userRole) {
  const destination = item?.deepLink || null;
  const creatorId = item?.creatorId || null;
  const isSubscriber = userRole === ROLE.USER;
  const hasCreatorProfileTarget = isSubscriber && Boolean(creatorId) && Boolean(item?.username);
  const isCardClickable =
    hasCreatorProfileTarget ||
    (isSubscriber && Boolean(creatorId) && Boolean(destination));

  return {
    destination,
    hasCreatorProfileTarget,
    isCardClickable,
  };
}

/**
 * When an unread row intersects the viewport, PATCH viewed once and let Redux flip isRead.
 */
function ViewTrackedActivityCard({
  item,
  tabId,
  grouped,
  isArchived = false,
  userRole,
  children,
  onClick,
  onKeyDown,
  isClickable = false,
}) {
  const dispatch = useDispatch();
  const rootRef = useRef(null);
  const firedRef = useRef(false);

  const viewIds = useMemo(() => {
    if (Array.isArray(item.unreadApiIds) && item.unreadApiIds.length) {
      return item.unreadApiIds;
    }
    if (item.apiNotificationId) return [item.apiNotificationId];
    return [];
  }, [item.unreadApiIds, item.apiNotificationId]);

  useEffect(() => {
    if (!tabId || tabId === 'archived' || isArchived) return;
    if (!viewIds.length) return;
    if (item.isRead !== false) return;

    const el = rootRef.current;
    if (!el) return;

    firedRef.current = false;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || firedRef.current) return;
        firedRef.current = true;
        dispatch(
          markActivityNotificationViewed(
            tabId,
            !!grouped,
            item.id,
            viewIds,
            userRole
          )
        ).then((res) => {
          if (!res || res.success === false) {
            firedRef.current = false;
          }
        });
      },
      { threshold: 0.15, root: null, rootMargin: '0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [
    dispatch,
    tabId,
    grouped,
    userRole,
    item.id,
    viewIds,
    item.isRead,
    isArchived,
  ]);

  return (
    <div
      ref={rootRef}
      className={`activity_card ${item.highlight ? 'highlighted' : ''} ${isClickable ? 'activity_card_clickable' : ''}`}
      onClick={onClick}
      onKeyDown={onKeyDown}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      {children}
    </div>
  );
}

const ActivityList = ({
  data = [],
  title,
  icon,
  colorPink,
  totalCount,
  tabId,
  grouped = false,
  isArchived = false,
}) => {
  const userRole = useMemo(getUserRole, []);
  const isCreator =
    userRole === ROLE.CREATOR_SUB || userRole === ROLE.CREATOR_CONTENT;
  const navigate = useNavigation();
  const dispatch = useDispatch();

  const sliceKey = isArchived ? ACTIVITY_ARCHIVED_SLICE_KEY : String(!!grouped);
  const activitySlice = useSelector(
    (state) =>
      state.activity?.byTab?.[tabId]?.byGrouped?.[sliceKey] ?? null
  );
  const currentPage = activitySlice?.pagination?.page ?? 1;

  const isPriorityMessagesTab = tabId === 'priority_messages';

  const handleExtendClick = useCallback(
    async (e, itemToExtend) => {
      e.stopPropagation();
      e.preventDefault();

      const tipId = itemToExtend?.tipId;
      if (!tipId) return;

      try {
        const result = await extendTipDeadline(tipId);
        if (!result?.success) return;

        dispatch(
          fetchCreatorActivityTab(tabId, {
            page: currentPage,
            limit: ACTIVITY_PAGE_LIMIT,
            append: false,
            role: userRole,
            grouped,
          })
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        reportError(error, 'ActivityList extend tip');
      }
    },
    [dispatch, tabId, currentPage, userRole, grouped]
  );

  if (!data.length) return null;

  return (
    <div
      className={`activity_card_wrapper ${colorPink ? 'color_pink_active' : ''} ${
        isPriorityMessagesTab ? 'activity_card_wrapper_priority_messages' : ''
      }`}
    >
      <div className="d-flex justify-content-between align-items-center mt-4 mb-3">
        <h2><i className={`fa-solid fa-${icon}`}></i> {title}</h2>
        {totalCount > 0 && (
          <div className="activity_head_count">{totalCount}</div>
        )}
      </div>
      {(colorPink && (userRole === ROLE.CREATOR_CONTENT || userRole === ROLE.CREATOR_SUB)) && (
        <p className="sub_heading">You have 10 days to reply or subs will be refunded</p>
      )}
      {colorPink && userRole === ROLE.USER && (
        <p className="sub_heading">Creator has 10 days to reply or you will be refunded</p>
      )}

      {data.map((item) => {
        const shouldUseIconOnly =
          tabId === 'sales_tips' ||
          tabId === 'purchases_tips' ||
          title === ACTIVITY_TITLE_MAP.purchases_tips;
        const canTapActivityUsername = Boolean(item?.username);
        const handleActivityUsernameClick = (e) => {
          e.stopPropagation();
          const mention = e?.currentTarget?.textContent?.trim();
          const targetUsername = mention?.startsWith('@')
            ? mention.slice(1)
            : item.username;
          if (!targetUsername) return;

          if (isCreator) {
            const subscriberUserId = resolveActivitySubscriberUserId(item);
            if (subscriberUserId && isMongoObjectIdString(subscriberUserId)) {
              navigate.toChat(subscriberUserId);
              return;
            }
          }

          navigate.toProfile(targetUsername);
        };

        const {
          destination: activityDestination,
          hasCreatorProfileTarget: canTapCreatorProfileCard,
          isCardClickable: canTapActivityCard,
        } = getActivityCardNavigationMeta(item, userRole);

        const handleCardClick = () => {
          if (canTapActivityCard) {
            if (canTapCreatorProfileCard) {
              navigate.toProfile(item.username);
              return;
            }
            if (activityDestination) {
              navigateToPushLink(activityDestination);
            }
          }
        };

        const handleCardKeyDown = (e) => {
          if (!canTapActivityCard) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCardClick();
          }
        };

        const daysLeftNum = parseDaysLeft(item.daysLeft);
        const shouldShowPriorityWarning =
          isPriorityMessagesTab &&
          Number.isFinite(daysLeftNum) &&
          daysLeftNum >= 0 &&
          daysLeftNum <= 5;
        const shouldShowExtend =
          isPriorityMessagesTab &&
          userRole === ROLE.USER &&
          item?.tipId &&
          !item?.isExtended &&
          Number.isFinite(daysLeftNum) &&
          daysLeftNum >= 0 &&
          daysLeftNum < 4;

        const avatarSrc = shouldUseIconOnly || item.forceActivityIcon
          ? null
          : (item.avatar ||
            (!item.showIconWhenAvatarMissing &&
            (ACTIVITY_TYPES_DEFAULT_AVATAR.has(item.type)
              ? DEFAULT_USER_AVATAR
              : null)));

        const cardInner = (
          <>
            {avatarSrc ? (
              <img
                src={avatarSrc}
                alt=""
                className="activity_card_avatar"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = DEFAULT_USER_AVATAR;
                }}
              />
            ) : (
              <span className="activity_icon">{getActivityIcon(item.iconType || item.type)}</span>
            )}

            <div className="activity_content">
              <div className="d-flex justify-content-between align-items-baseline">

                {/* Title / Username */}
                {isPriorityMessagesTab ? (
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <h3 className="activity_priority_username">
                      <span
                        className={canTapActivityUsername ? 'activity_username cursor-pointer' : ''}
                        onClick={canTapActivityUsername ? handleActivityUsernameClick : undefined}
                      >
                        {item.username ? `@${item.username}` : item.type}
                      </span>
                      {shouldShowPriorityWarning ? (
                        <i
                          className="fa-solid fa-triangle-exclamation activity_priority_warning_icon"
                          aria-label="Reply deadline warning"
                          title="Reply deadline warning"
                        />
                      ) : null}
                    </h3>

                    {shouldShowExtend && (
                      <button
                        type="button"
                        className="activity_priority_extend_btn"
                        onClick={(e) => handleExtendClick(e, item)}
                        aria-label="Extend reply deadline"
                      >
                        Extend
                      </button>
                    )}
                  </div>
                ) : (
                  <h3 className={canTapActivityCard ? 'activity_title_clickable' : ''}>
                    {TITLES_WITH_INLINE_MENTIONS.has(title) ? (
                      <span>
                        {renderTitleWithMentions(
                          item.title,
                          handleActivityUsernameClick
                        )}
                      </span>
                    ) : (
                      <>
                        {item.username ? (
                          <>
                            <span
                              className={
                                canTapActivityUsername
                                  ? 'activity_username cursor-pointer'
                                  : title === ACTIVITY_TITLE_MAP.tags
                                    ? 'activity_username'
                                    : ''
                              }
                              onClick={canTapActivityUsername ? handleActivityUsernameClick : undefined}
                            >
                              @{item.username}{' '}
                            </span>
                            {title === ACTIVITY_TITLE_MAP.tags ? 'tagged you' : ''}
                          </>
                        ) : item.title ? (
                          item.title
                        ) : (
                          item.type
                        )}
                      </>
                    )}
                    {daysLeftNum != null && daysLeftNum > 0 && daysLeftNum < 3 && (
                      <span> ⚠️</span>
                    )}
                  </h3>
                )}

                <span className="time">
                  {item.isRead === false ? (
                    <span className="activity_unread_dot" aria-hidden="true" />
                  ) : null}
                  {item.timeAgo}
                </span>
              </div>

              {/* Description / Message — skip when API echoes title as message (grouped feeds) */}
              {shouldShowActivityDescription(item, title, {
                isPriorityTab: isPriorityMessagesTab,
              }) ? (
                <p>
                  {renderActivityMessage(
                    item,
                    canTapActivityUsername ? handleActivityUsernameClick : undefined
                  )}
                </p>
              ) : null}

              {getActivityBadge(title, item, userRole)}

              {title === ACTIVITY_TITLE_MAP.promotions &&
              item.promoRenewalMonthlyPriceFormatted ? (
                <div className="mt-2">
                  <button
                    type="button"
                    className="activity_promo_renewal_btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (canTapActivityCard) handleCardClick();
                    }}
                  >
                    <span className="activity_promo_renewal_btn_prefix">Renew for</span>
                    <span className="activity_promo_renewal_price">
                      ${item.promoRenewalMonthlyPriceFormatted}/mo
                    </span>
                  </button>
                </div>
              ) : null}

              {/* PPV / Price (New Posts) */}
              {item.isPPV &&
              Number(item.price) > 0 ? (
                <span className="ppv_badge">
                  <i className="fa-solid fa-lock" aria-hidden="true"></i>
                  <span>PPV content</span>
                  <strong>${formatAmountWithTwoDecimalPlaces(item.price)}</strong>
                </span>
              ) : null}

              {/* CTA button (Promotions style) */}
              {item.cta && (
                <button className="activity_pink_badge mt-2">{item.cta}</button>
              )}

              {/* Refund amount (Purchases & Tips style) */}
              {item.refundAmount && (
                <span className="refund_badge">Refunded ${item.refundAmount}</span>
              )}
            </div>
          </>
        );

        return (
          <ViewTrackedActivityCard
            key={item.id}
            item={item}
            tabId={tabId}
            grouped={grouped}
            isArchived={isArchived}
            userRole={userRole}
            onClick={canTapActivityCard ? handleCardClick : undefined}
            onKeyDown={canTapActivityCard ? handleCardKeyDown : undefined}
            isClickable={canTapActivityCard}
          >
            {cardInner}
          </ViewTrackedActivityCard>
        );
      })}
    </div>
  );
};

export default ActivityList;
